import { Component, OnInit, Type } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AccountingSystem , AccountingSystemDetails, AddressResource, AccountingResource, OrganizationResource, SettingsResource, AccountingSettings } from '@apaq/orgkit-js';
import { debounceTime } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { BillyConnectionDialogComponent } from './connections-dialogs/billy-connection-dialog/billy-connection-dialog.component';
import { DineroConnectionDialogComponent } from './connections-dialogs/dinero-connection-dialog/dinero-connection-dialog.component';
import { EconomicConnectionDialogComponent } from './connections-dialogs/economic-connection-dialog/economic-connection-dialog.component';
import { SystemSelectionDialogComponent } from './system-selection-dialog/system-selection-dialog.component';

export interface ConnectDetails {
  system: AccountingSystem;
  systemId: string;
  systemKey: string;
}

@Component({
  selector: 'app-accounting-view',
  templateUrl: './accounting-view.component.html',
  styleUrls: ['./accounting-view.component.scss']
})
export class AccountingViewComponent implements OnInit {

  form: FormGroup;
  _enabled = false;
  checkingConnection = false;
  initialized = false;
  systemValidationPromise?: Promise<ValidationErrors | null>;

  accountingSystems = [
    { id: 'Billy', name: 'Billy' },
    { id: 'Dinero', name: 'Dinero' },
    { id: 'Economic', name: 'E-conomic' }
  ];

  constructor(private fb: FormBuilder, private dialogService: DialogService,
    private organizationService: OrganizationResource,
    private addressService: AddressResource,
    private settingsResource: SettingsResource,
    private accountingResource: AccountingResource) {
    this.form = this.fb.group({
      accountingMode: [''],
      system: ['', [Validators.required]],
      systemId: ['', [this.systemIdRequired]],
      systemKey: ['', [this.systemKeyRequired], [this.validateSystemKey.bind(this)]],
      accountingTime: [''],
      invoiceDelivery: [''],
      synchronizationDisabled: [false]
    });

    this.form.get('system')?.valueChanges.subscribe(value => {
      this.form.get('systemId')!.setValue('');
      this.form.get('systemKey')!.setValue('');

      this.setSettingsEnabled(value !== 'None');
    });

    this.form.get('accountingMode')?.valueChanges.subscribe(value => {
      if (value === 'None' || !this.isSystemDefined()) {
        this.form.get('accountingTime')!.disable();
        this.form.get('invoiceDelivery')!.disable();
      } else {
        this.form.get('accountingTime')!.enable();
        this.form.get('invoiceDelivery')!.enable();
      }

    });
  }

  get enabled() {
    return this._enabled;
  }

  set enabled(value) {
    if (value === false && this.isSystemDefined()) {
      this.disconnectSystem().then(_ => {
        this._enabled = this.isSystemDefined();
      });
    }
    this._enabled = value;
    
  }

  isInitialized() {
    return this.initialized;
  }

  isSystemDefined() {
    return this.form.get('system')?.value != 'None';
  }

  isSystemUnableToConnect() {
    return !this.form.get('systemKey')?.valid;
  }

  isAccountingModeDefined() {
    return this.form.get('accountingMode')?.value != 'None';
  }

  async ngOnInit() {
    const settings = await this.settingsResource.findById('accounting');
    this.form.patchValue(settings);
    this.enabled = this.isSystemDefined();
    this.initialized = true;

    this.form.valueChanges.pipe(debounceTime(350)).subscribe(_ => {
      this.settingsResource.saveAs(this.form.value, 'accounting');
    });
  }

  connectSystem() {
    this.dialogService.open(SystemSelectionDialogComponent, $localize`Connect to accounting system`, { headerStyle: 'hidden' }).afterClosed().subscribe((result: AccountingSystem | undefined) => {
      if (result) {
        let dialog: Type<any> | undefined = this.resolveConnectDialog(result);
        if (dialog) {
          this.showConnectDialog(dialog, result);
        }
      }
    });
  }

  private showConnectDialog(dialog: Type<any>, system: AccountingSystem) {
    const data: ConnectDetails = {
      system,
      systemId: this.form.get('systemId')?.value,
      systemKey: this.form.get('systemKey')?.value
    };

    this.dialogService.open(dialog, $localize`Connect to ${system}`, { data, headerStyle: 'hidden' }).afterClosed().subscribe(async (details: AccountingSystemDetails) => {
      if (details) {
        this.form.get('system')?.setValue(details.system);
        this.form.get('systemId')?.setValue(details.id);
        this.form.get('systemKey')?.setValue(details.key);
        this.form.get('synchronizationDisabled')?.setValue(false);
      } else {
        this.doDisconnect();
      }

    });
  }

  private resolveConnectDialog(result: string) {
    let dialog: Type<any> | undefined;
    switch (result) {
      case 'Billy':
        dialog = BillyConnectionDialogComponent;
        break;
      case 'Dinero':
        dialog = DineroConnectionDialogComponent;
        break;
      case 'Economic':
        dialog = EconomicConnectionDialogComponent;
        break;
    }
    return dialog;
  }

  async disconnectSystem() {
    if (await Stallion.confirm($localize`Are you sure you want to disconnect your accounting system from Previsto?`, 'danger', undefined, $localize`Yes, disconnect now`)) {
      this.doDisconnect();
    }
  }

  reconnectSystem() {
    const system = this.form.get('system')?.value;
    const dialog = this.resolveConnectDialog(system);
    if(dialog) this.showConnectDialog(dialog, system);
  }

  doDisconnect() {
    this.form.get('system')?.setValue('None');
    this.form.get('systemId')?.setValue(null);
    this.form.get('systemKey')?.setValue(null);
    this.form.get('synchronizationDisabled')?.setValue(false);
  }

  systemIdRequired(control: AbstractControl): ValidationErrors | undefined {
    const systemControl = control.root.get('system');
    const idControl = control.root.get('systemId');
    if (systemControl != null && systemControl.value === 'Dinero' && (idControl == null || idControl.value == null || idControl.value.trim() === '')) {
      return { systemId: { value: null } };
    }

    return undefined;
  }

  systemKeyRequired(control: AbstractControl): ValidationErrors | undefined {
    const systemControl = control.root.get('system');
    const keyControl = control.root.get('systemKey');
    if (systemControl != null && systemControl.value !== 'None' && (keyControl == null || keyControl.value.trim() === '')) {
      return { systemKey: { value: null } };
    }

    return undefined;
  }

  validateSystemKey(control: AbstractControl): Promise<ValidationErrors | null> {
    if(this.systemValidationPromise) {
      return this.systemValidationPromise;
    }
    this.checkingConnection = true;
    const systemControl = control.root.get('system');
    const idControl = control.root.get('systemId');
    const key = control.value;

    if (systemControl == null || systemControl.value === '' || systemControl.value === 'None' || key === '') {
      this.checkingConnection = false;
      return Promise.resolve(null);
    }

    const system = systemControl.value;
    const id = idControl ? idControl.value : null;

    this.systemValidationPromise = this.accountingResource.checkAccountingBackend({ id, key, system })
      .then(_ => null)
      .catch(error => { 
        return {systemKey: { error, value: key.value }};
      })
      .finally(() => {
        this.checkingConnection = false;
        this.systemValidationPromise = undefined;
      });
    return this.systemValidationPromise;
  }

  private setSettingsEnabled(value: boolean) {
    const formAccountingMode = this.form.get('accountingMode');
    const formAccountingTime = this.form.get('accountingTime');
    const formInvoiceDelivery = this.form.get('invoiceDelivery');

    if (value === true) {
      formAccountingMode?.enable();
      formAccountingTime?.enable();
      formInvoiceDelivery?.enable();
    } else {
      formAccountingMode?.disable();
      formAccountingTime?.disable();
      formInvoiceDelivery?.disable();
    }
  }

}
